import { useEffect, useMemo, useState, useRef } from "react";
import { UploadCloud, Loader2, X, Download, Plus } from "lucide-react";
import { motion } from "framer-motion";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import {
  BusinessService,
  ServiceStaffMember,
} from "../../types/businessService";
import { Employee } from "../../types/employee";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../hooks/useToast";

type DiscountType = "percent" | "fixed";
type TimeUnit = "minutes" | "hours";

export interface ServiceFormInput {
  name: string;
  slug: string;
  price: number;
  discount: number;
  discountType: DiscountType;
  priceAfterDiscount: number;
  duration: number;
  timeUnit: TimeUnit;
  staff: ServiceStaffMember[];
  status: "active" | "inactive";
  categoryId: string;
  description: string;
  taxes: number[];
  enableDeposit: boolean;
  depositType: "fixed" | "percent";
  depositAmount: number;
  websiteVisibility: boolean;
}

export interface Tax {
  id: number;
  name: string;
  percent: number;
  status: string;
}

interface ServiceImage {
  name: string;
  size: number;
  type: string;
  url: string;
}

interface ServiceModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  service?: BusinessService | null;
  onClose: () => void;
  onSave: (data: ServiceFormInput, images?: { newFiles: File[]; existingImages: string[]; defaultImage?: string }) => void;
  categories: { id: string; name: string }[];
  activeCategory: string;
  employees: Employee[];
  employeesLoading?: boolean;
  categoriesLoading?: boolean;
  taxes?: Tax[];
  taxesLoading?: boolean;
  existingImages?: ServiceImage[];
  saving?: boolean;
}

export const ServiceModal = ({
  isOpen,
  mode,
  service,
  onClose,
  onSave,
  categories = [],
  activeCategory,
  employees = [],
  employeesLoading = false,
  categoriesLoading = false,
  taxes = [],
  taxesLoading = false,
  existingImages = [],
  saving = false,
}: ServiceModalProps) => {
  const { user } = useAuth();
  const toast = useToast();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  // Ensure categories is always an array
  const safeCategories = Array.isArray(categories) ? categories : [];

  const [formData, setFormData] = useState<ServiceFormInput>({
    name: "",
    slug: "",
    price: 0,
    discount: 0,
    discountType: "percent",
    priceAfterDiscount: 0,
    duration: 30,
    timeUnit: "minutes",
    staff: [],
    status: "active",
    categoryId: "",
    description: "",
    taxes: [],
    enableDeposit: false,
    depositType: "fixed",
    depositAmount: 0,
    websiteVisibility: true,
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [currentImages, setCurrentImages] = useState<ServiceImage[]>([]);
  const [defaultImage, setDefaultImage] = useState<string | null>(null);

  // Bulk Upload State
  const [modalMode, setModalMode] = useState<"single" | "bulk">("single");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isCsvDragOver, setIsCsvDragOver] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (service) {
        const initialTimeUnit =
          service.timeUnit ||
          (service.duration >= 60 && service.duration % 60 === 0
            ? "hours"
            : "minutes");
        const timeValue =
          initialTimeUnit === "hours"
            ? Math.max(1, Math.round((service.duration || 60) / 60))
            : service.duration || 30;
        setFormData({
          name: service.name,
          slug: service.slug || "",
          price: service.price || 0,
          discount: service.discount || 0,
          discountType:
            (service.discountType as DiscountType) || ("percent" as DiscountType),
          priceAfterDiscount: service.discountPrice || service.price || 0,
          duration: timeValue,
          timeUnit: initialTimeUnit as TimeUnit,
          staff: service.staff ?? [],
          status: service.status,
          categoryId: service.categoryId || activeCategory,
          description: service.description || "",
          taxes: (() => {
            // First check if 'taxes' exists directly (legacy or other format)
            if (service.taxes && Array.isArray(service.taxes) && service.taxes.length > 0) {
              return service.taxes.map(t => typeof t === 'string' ? parseInt(t) : t).filter(id => !isNaN(id) && id > 0);
            }

            // Then check for 'selectedTax' which is common in the current API response format
            const rawService = service as any;
            if (rawService.selectedTax && Array.isArray(rawService.selectedTax)) {
              return rawService.selectedTax
                .map((st: any) => typeof st.tax_id === 'string' ? parseInt(st.tax_id) : st.tax_id)
                .filter((id: number) => !isNaN(id) && id > 0);
            }

            return [];
          })(),
          enableDeposit: service.enableDeposit ?? false,
          depositType: service.depositType === "percent" ? "percent" : "fixed",
          depositAmount: service.depositAmount ?? 0,
          websiteVisibility: service.websiteVisibility !== "hide",
        });
        setSlugManuallyEdited(Boolean(service.slug));
        // Images will be set from existingImages prop
        setCurrentImages(existingImages || []);
      } else {
        // For add mode, use activeCategory if available, otherwise use first category
        const defaultCatId = activeCategory || (safeCategories.length > 0 ? safeCategories[0].id : "");
        setFormData({
          name: "",
          slug: "",
          price: 0,
          discount: 0,
          discountType: "percent",
          priceAfterDiscount: 0,
          duration: 30,
          timeUnit: "minutes",
          staff: [],
          status: "active",
          categoryId: defaultCatId,
          description: "",
          taxes: [],
          enableDeposit: false,
          depositType: "fixed",
          depositAmount: 0,
          websiteVisibility: true,
        });
        setSlugManuallyEdited(false);
        setUploadedFiles([]);
        setCurrentImages([]);
        setDefaultImage(null);
      }
    }
  }, [isOpen, service, activeCategory, categories, existingImages]);

  useEffect(() => {
    if (!slugManuallyEdited) {
      const generated = formData.name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({
        ...prev,
        slug: generated,
      }));
    }
  }, [formData.name, slugManuallyEdited]);

  const priceAfterDiscount = useMemo(() => {
    const price = Number(formData.price) || 0;
    const discount = Number(formData.discount) || 0;
    if (formData.discountType === "percent") {
      return Math.max(price - price * (discount / 100), 0);
    }
    return Math.max(price - discount, 0);
  }, [formData.price, formData.discount, formData.discountType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      formData.name.trim() &&
      formData.duration > 0 &&
      formData.price >= 0
    ) {
      const durationInMinutes =
        formData.timeUnit === "hours"
          ? formData.duration * 60
          : formData.duration;


      if (durationInMinutes < 15) {
        toast.error("Service duration must be at least 15 minutes.");
        return;
      }

      // Discount validation
      if (formData.discount > 0) {
        if (formData.discountType === "percent" && formData.discount > 100) {
          toast.error("Discount percentage cannot exceed 100%");
          return;
        }
        if (formData.discountType === "fixed" && formData.discount > formData.price) {
          toast.error("Discount amount cannot exceed the service price");
          return;
        }
      }

      // Generate slug from name if empty
      const slug = formData.slug.trim() || formData.name.trim().toLowerCase().replace(/\s+/g, "-");

      // Prepare image data
      const existingImageNames = currentImages.map(img => img.name);
      const imageData = {
        newFiles: uploadedFiles,
        existingImages: existingImageNames,
        defaultImage: defaultImage || undefined,
      };

      onSave({
        name: formData.name.trim(),
        slug: slug,
        price: formData.price,
        discount: formData.discount,
        discountType: formData.discountType,
        priceAfterDiscount,
        duration: durationInMinutes,
        timeUnit: formData.timeUnit,
        staff: formData.staff,
        status: formData.status,
        categoryId: formData.categoryId,
        description: formData.description,
        taxes: formData.taxes,
        enableDeposit: formData.enableDeposit,
        depositType: formData.depositType,
        depositAmount: formData.enableDeposit ? formData.depositAmount : 0,
        websiteVisibility: formData.websiteVisibility,
      }, imageData);
    }
  };

  const handleStaffToggle = (employee: Employee) => {
    const employeeId = String(employee.id);
    setFormData((prev) => {
      const exists = prev.staff.some((member) => member.id === employeeId);
      const updatedStaff = exists
        ? prev.staff.filter((member) => member.id !== employeeId)
        : [
          ...prev.staff,
          {
            id: employeeId,
            name: employee.name,
          },
        ];
      return { ...prev, staff: updatedStaff };
    });
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (imageName: string) => {
    setCurrentImages(prev => prev.filter(img => img.name !== imageName));
    if (defaultImage === imageName) {
      setDefaultImage(null);
    }
  };

  const handleSetDefaultImage = (imageName: string) => {
    setDefaultImage(imageName);
  };

  // CSV Drag and Drop Handlers
  const handleCsvDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCsvDragOver(true);
  };

  const handleCsvDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCsvDragOver(false);
  };

  const handleCsvDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCsvDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].name.endsWith(".csv")) {
      setCsvFile(files[0]);
    } else {
      toast.error("Please upload a valid CSV file");
    }
  };

  const handleCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (files[0].name.endsWith(".csv")) {
        setCsvFile(files[0]);
      } else {
        toast.error("Please upload a valid CSV file");
      }
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error("Please select a CSV file to upload");
      return;
    }

    // Since we don't have a direct service function for this yet, we use api directly
    // Similar to ProductForm handles it
    try {
      const api = (await import("../../services/api")).default;
      const formData = new FormData();
      formData.append("file", csvFile);

      const response = await api.post("/admin/business-services/bulk-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(response.data?.message || "Services uploaded successfully!");
      onClose();
      // We might need to refresh the list, but ServiceModal doesn't have a direct refresh prop
      // It depends on the parent handling successfully saved events.
      // For now, closing the modal is the standard behavior in ProductForm.
      // The parent ServicesPage already handles refresh after onSave.
      // But bulk upload is an independent API call here.
      // In ServicesPage.tsx, fetchServices() would need to be called.
      // Since we don't have that here, we trigger onClose and assume user will refresh or 
      // the parent might have a mechanism.
      window.location.reload(); // Simple way to ensure refresh for bulk actions
    } catch (error: any) {
      console.error("Bulk upload error:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to upload services. Please check your CSV file and try again.";
      toast.error(errorMessage);
    }
  };

  const handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = 'https://salonify.co.uk/csv-samples/services.csv';
    link.download = 'services.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 z-[1000]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-4xl p-0 flex flex-col max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-2 border-b flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {modalMode === "single"
                ? (mode === "add" ? "Add New Service" : "Edit Service")
                : "Bulk Upload Services"}
            </h2>
            <div className="flex items-center gap-2">
              {modalMode === "bulk" && (
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#885ABB] bg-white rounded-lg hover:bg-purple-50 transition-colors border border-[#885ABB]"
                >
                  <Download className="w-4 h-4" />
                  <span>Sample</span>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-[#885ABB] hover:bg-purple-50 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {mode === "add" && (
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit">
              <button
                type="button"
                onClick={() => setModalMode("single")}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${modalMode === "single"
                  ? "bg-white text-[#885ABB] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <Plus className="w-4 h-4" />
                Add Service
              </button>
              <button
                type="button"
                onClick={() => setModalMode("bulk")}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${modalMode === "bulk"
                  ? "bg-white text-[#885ABB] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <UploadCloud className="w-4 h-4" />
                Bulk Upload
              </button>
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <form
          onSubmit={modalMode === "single" ? handleSubmit : handleBulkSubmit}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
        >
          {modalMode === "single" ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Service Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="e.g. Hair Cut"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Service Slug<span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true);
                      setFormData((prev) => ({ ...prev, slug: e.target.value }));
                    }}
                    placeholder="hair-cut"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Price ({currencySymbol})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price || ""}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        price: val === "" ? 0 : parseFloat(val),
                      }));
                    }}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-700">
                      Discount
                    </label>
                    <div className="inline-flex rounded-lg border border-gray-300">
                      {(["percent", "fixed"] as DiscountType[]).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, discountType: type }))
                          }
                          className={`px-3 py-1 text-xs font-medium ${formData.discountType === type
                            ? "bg-[#885ABB] text-white"
                            : "text-gray-600"
                            }`}
                        >
                          {type === "percent" ? "Percent" : "Fixed"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.discount || ""}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        discount: val === "" ? 0 : parseFloat(val),
                      }));
                    }}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Price after discount ({currencySymbol})
                  </label>
                  <input
                    value={priceAfterDiscount.toFixed(2)}
                    readOnly
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Category
                  </label>
                  <Select
                    value={formData.categoryId}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, categoryId: value }))
                    }
                    disabled={categoriesLoading}
                  >
                    <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      <SelectValue placeholder={categoriesLoading ? "Loading categories..." : "Select category"} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                      {categoriesLoading ? (
                        <div className="px-2 py-1.5 text-sm text-gray-500">
                          Loading categories...
                        </div>
                      ) : safeCategories.length > 0 ? (
                        safeCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-1.5 text-sm text-gray-500">
                          No categories available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Time required
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.duration || ""}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        duration: val === "" ? 0 : parseInt(val),
                      }));
                    }}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Time unit
                  </label>
                  <Select
                    value={formData.timeUnit}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        timeUnit: value as TimeUnit,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Select Tax
                  </label>
                  {taxesLoading ? (
                    <div className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-500">
                      Loading taxes...
                    </div>
                  ) : taxes.length > 0 ? (
                    <div className="mt-1 space-y-2">
                      <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                        {taxes.map((tax) => {
                          const isSelected = formData.taxes.includes(tax.id);
                          return (
                            <label
                              key={tax.id}
                              className={`flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${isSelected
                                ? "border-[#885ABB] bg-[#f6f2ff]"
                                : "border-gray-200 hover:bg-gray-50"
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    taxes: isSelected
                                      ? prev.taxes.filter(id => id !== tax.id)
                                      : [...prev.taxes, tax.id],
                                  }));
                                }}
                                className="rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                              />
                              <span className="flex-1">{tax.name} ({tax.percent}%)</span>
                            </label>
                          );
                        })}
                      </div>
                      {formData.taxes.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.taxes.map((taxId) => {
                            const tax = taxes.find(t => t.id === taxId);
                            return tax ? (
                              <span
                                key={taxId}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#f6f2ff] text-[#885ABB] rounded-md text-xs"
                              >
                                {tax.name} ({tax.percent}%)
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      taxes: prev.taxes.filter(id => id !== taxId),
                                    }));
                                  }}
                                  className="hover:text-[#6f47a2]"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-500">
                      No taxes available
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Status
                  </label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: value as "active" | "inactive",
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Website Visibility
                  </label>
                  <div className="mt-1 h-[42px] rounded-lg border border-gray-300 px-4 flex items-center">
                    <label className="inline-flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.websiteVisibility}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            websiteVisibility: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 shrink-0 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB] cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Show on website</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Enable Deposit */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    id="enable-deposit-checkbox"
                    type="checkbox"
                    checked={formData.enableDeposit}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        enableDeposit: e.target.checked,
                        ...(e.target.checked ? {} : { depositAmount: 0 }),
                      }))
                    }
                    className="w-4 h-4 shrink-0 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB] cursor-pointer"
                  />
                  <label
                    htmlFor="enable-deposit-checkbox"
                    className="text-sm font-semibold text-gray-700 cursor-pointer select-none"
                  >
                    Enable deposit
                  </label>
                </div>
                {formData.enableDeposit && (
                  <div className="grid grid-cols-2 gap-4 pl-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Deposit type
                      </label>
                      <Select
                        value={formData.depositType}
                        onValueChange={(value: "fixed" | "percent") =>
                          setFormData((prev) => ({
                            ...prev,
                            depositType: value,
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                          <SelectItem value="fixed">Fixed</SelectItem>
                          <SelectItem value="percent">Percent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Deposit amount
                        {formData.depositType === "percent" ? " (%)" : ` (${currencySymbol})`}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step={formData.depositType === "percent" ? 1 : 0.01}
                        value={formData.depositAmount === 0 ? "" : formData.depositAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            depositAmount: val === "" ? 0 : parseFloat(val) || 0,
                          }));
                        }}
                        placeholder={formData.depositType === "percent" ? "e.g. 20" : "e.g. 50"}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">
                    Assign Employee
                  </label>
                  <span className="text-xs text-gray-400">
                    {formData.staff.length} selected
                  </span>
                </div>
                {employeesLoading ? (
                  <p className="mt-3 text-sm text-gray-500">Loading employees...</p>
                ) : employees.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">
                    No team members available. Add employees first.
                  </p>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {employees.map((employee) => {
                      const employeeId = String(employee.id);
                      const checked = formData.staff.some(
                        (member) => member.id === employeeId
                      );
                      return (
                        <label
                          key={employeeId}
                          className={`flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm ${checked
                            ? "border-[#885ABB] bg-[#f6f2ff]"
                            : "border-gray-200"
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleStaffToggle(employee)}
                            className="rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                          />
                          <span className="truncate">{employee.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Service Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={5}
                  placeholder="Describe the service..."
                  className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Service Media
                </label>

                {/* Existing Images */}
                {currentImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-3">
                    {currentImages.map((image) => (
                      <div key={image.name} className="relative group">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingImage(image.name)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {defaultImage === image.name && (
                          <div className="absolute bottom-1 left-1 px-2 py-1 bg-[#885ABB] text-white text-xs rounded">
                            Default
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSetDefaultImage(image.name)}
                          className="absolute bottom-1 right-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          Set Default
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Area */}
                <label className="mt-2 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500 cursor-pointer hover:border-[#885ABB] hover:text-[#885ABB] transition-colors">
                  <UploadCloud className="mb-2 h-8 w-8" />
                  <p className="font-medium">Drop files here to upload</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Recommended resolution 1920 × 1080
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelection}
                    className="hidden"
                  />
                </label>

                {/* New Uploaded Files Preview */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-gray-600">New files to upload:</p>
                    <div className="flex flex-wrap gap-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                          <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveUploadedFile(index)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </>
          ) : (
            <div className="py-10">
              <div
                onDragOver={handleCsvDragOver}
                onDragLeave={handleCsvDragLeave}
                onDrop={handleCsvDrop}
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${isCsvDragOver
                  ? "border-[#885ABB] bg-purple-50"
                  : "border-gray-200 bg-gray-50"
                  }`}
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100">
                  <UploadCloud className="h-8 w-8 text-[#885ABB]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Upload Services CSV
                </h3>
                <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
                  Drag and drop your services CSV file here, or click to browse your files.
                </p>
                <input
                  type="file"
                  ref={csvInputRef}
                  onChange={handleCsvFileSelect}
                  accept=".csv"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => csvInputRef.current?.click()}
                  className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm"
                >
                  Browse Files
                </button>

                {csvFile && (
                  <div className="mt-6 p-4 bg-white rounded-xl border border-purple-100 flex items-center justify-between max-w-md mx-auto">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-50 rounded-lg">
                        <UploadCloud className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900 truncate max-w-[200px]">
                          {csvFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(csvFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCsvFile(null)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={modalMode === "single" ? (!formData.name.trim() || saving) : (!csvFile || saving)}
              className="px-6 py-2 rounded-xl bg-[#885ABB] hover:bg-[#6f47a2] text-white transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{modalMode === "single" ? "Saving..." : "Uploading..."}</span>
                </>
              ) : (
                <span>
                  {modalMode === "single"
                    ? (mode === "add" ? "Add Service" : "Save Changes")
                    : "Upload Services"}
                </span>
              )}
            </button>
          </div>
        </form >
      </div >
    </div >
  );
};
